import { Link } from 'react-router-dom';
import { VirtuosoGrid } from 'react-virtuoso';
import useAppScrollParent from '@/hooks/useAppScrollParent';
import CasinoCardSkeleton from '@/components/common/CasinoCardSkeleton';
import CasinoGameCard from '@/components/common/CasinoGameCard';
import { CasinoGame } from '@/types/casino';

interface Props {
  sections: {
    title: string;
    games: CasinoGame[];
  }[];
  isLoading?: boolean;
  onGameClick?: (game: CasinoGame) => void;
}

export default function CasinoGameGrid({ sections, isLoading = false, onGameClick }: Props) {
  const scrollParent = useAppScrollParent();
  if (isLoading) {
    return (
      <div className="flex-1">
        <div className="h-[2.125rem] w-40 animate-pulse rounded bg-gradient-to-r from-slate-200 via-gray-100 to-gray-200" />
        <div className="mt-2 grid grid-cols-3 gap-x-2 gap-y-2">
          <CasinoCardSkeleton />
        </div>
      </div>
    );
  }

  if (sections.length === 0) {
    return (
      <div className="flex-1 rounded-xl bg-white px-4 py-8 text-center text-base font-medium text-slate-500">
        No casino games available for this category.
      </div>
    );
  }

  return (
    <div className="flex-1 space-y-1">
      {sections.map((section) => (
        <section key={section.title}>
          <header className="mb-2 flex h-[2.125rem] items-center font-bold">
            <div className="mr-3 h-5 w-1 rounded-lg bg-black" />
            <span className="text-lg text-black">{section.title}</span>
          </header>

          <VirtuosoGrid
            customScrollParent={scrollParent}
            data={section.games}
            listClassName="grid grid-cols-3 gap-2"
            computeItemKey={(_, game) => game.id}
            itemContent={(_, game) => <CasinoGameCard game={game} onClick={onGameClick} />}
          />
        </section>
      ))}

      <div className={'flex justify-center py-4'}>
        <Link
          to={'/casino'}
          className="px-4 py-1.5 font-medium rounded flex gap-x-3 justify-center items-center
               bg-black/85 text-white"
        >
          Show More <i className="icon-arrow-down -rotate-90 text-base" />
        </Link>
      </div>
    </div>
  );
}
